import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, 
  TableContainer, Button, Chip, IconButton, Alert, CircularProgress, Autocomplete, TextField 
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import api from '../../api';

const MeetingOrganizers = () => {
  const [organizers, setOrganizers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orgRes, empRes] = await Promise.all([
        api.get('/meeting-masters/organizers'),
        api.get('/employees')
      ]);
      setOrganizers(orgRes.data);
      setEmployees(empRes.data.data || empRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to fetch data. Ensure you have Admin rights.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAddOrganizer = async () => {
    if (!selectedEmp) return setMessage({ type: 'error', text: 'Please select an employee first.' });
    setSubmitting(true);
    try {
      await api.post('/meeting-masters/organizers', { employee_id: selectedEmp.emp_id });
      setMessage({ type: 'success', text: 'Organizer added successfully.' });
      setSelectedEmp(null);
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add organizer.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveOrganizer = async (orgId) => {
    if (!window.confirm("Are you sure you want to remove Meeting Organizer access for this employee?")) return;
    try {
      await api.delete(`/meeting-masters/organizers/${orgId}`);
      setMessage({ type: 'success', text: 'Organizer removed.' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to remove.' });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, margin: '0 auto' }}>
      <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
        Manage Meeting Organizers
      </Typography>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({type:'', text:''})}>{message.text}</Alert>}

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Autocomplete
          options={employees}
          getOptionLabel={(option) => `${option.emp_id} - ${option.first_name} ${option.last_name}`}
          value={selectedEmp}
          onChange={(e, newValue) => setSelectedEmp(newValue)}
          renderInput={(params) => <TextField {...params} label="Search Employee" size="small" />}
          sx={{ flexGrow: 1 }}
        />
        <Button 
          variant="contained" 
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Add />} 
          onClick={handleAddOrganizer}
          disabled={submitting || !selectedEmp}
        >
          Add Organizer
        </Button>
      </Paper>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? <CircularProgress sx={{ display: 'block', m: 4, mx: 'auto' }} /> : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Added Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizers.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{org.employee_id}</TableCell>
                    <TableCell>{org.employee_name}</TableCell>
                    <TableCell>{new Date(org.added_datetime).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip size="small" label={org.is_active ? 'Active' : 'Removed'} color={org.is_active ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell align="center">
                      {org.is_active && (
                        <IconButton size="small" color="error" onClick={() => handleRemoveOrganizer(org.id)} title="Remove Access">
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {organizers.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">No organizers configured yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default MeetingOrganizers;